import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import styles from './PageSearch.module.scss';
import coveoSearch from '../../apis/coveo';
import { SearchInputUI } from '../SearchInput';
import ElementLink from '../ElementLink';
import Asset from '../Asset';
import { SearchControls } from './SearchControls';
import { FilterCard, reduceFilters, getInitialFilters, translateFiltersToQuery } from './SearchFilters';
import { getCommonString } from '../../utils/i18n';
import Breadcrumb from '../Breadcrumb';

// These are pretty much straight from Coveo, except `type`.
export const SearchResultItemPropTypes = {
  title: PropTypes.string.isRequired,
  uri: PropTypes.string.isRequired,
  icon: PropTypes.string,
  excerpt: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['topic', 'video', 'liveSession', 'selfGuided', 'unknown']).isRequired
};

function SearchResultItem({ title, uri, icon, excerpt, type }) {
  const { t } = useTranslation();
  return (
    <div className={styles.searchResult}>
      <div className={styles.searchResultIcon}>
        {icon && <Asset url={icon} />}
        <p className={styles.searchResultType}>
          {type !== 'unknown' ? getCommonString(`searchResultType-${type}`, t) : ''}
        </p>
      </div>
      <div className={styles.searchResultText}>
        <ElementLink
          className={styles.searchResultLink}
          href={uri}
          isModal={false}
          download={false}
          linkText={title}
          trackingId={`page_search_${new URL(uri).pathname}`}
          data-testid="Search-LinkTitle"
        />
        <p className={styles.searchResultExcerpt}>{excerpt}</p>
      </div>
    </div>
  );
}

SearchResultItem.propTypes = SearchResultItemPropTypes;
SearchResultItem.defaultProps = {
  icon: undefined
};

export const SearchResultsPropTypes = {
  results: PropTypes.arrayOf(PropTypes.shape(SearchResultItemPropTypes)).isRequired
};

function SearchResults({ results }) {
  return (
    <ul className={styles.searchResultList}>
      {results.map((result) => (
        <SearchResultItem
          key={result.uri}
          title={result.title}
          uri={result.uri}
          icon={result.icon}
          excerpt={result.excerpt}
          type={result.type}
        />
      ))}
    </ul>
  );
}

SearchResults.propTypes = SearchResultsPropTypes;

export const PageSearchPropTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  searchImages: PropTypes.object.isRequired
};

const applyResultType = (result, searchImages) => {
  const detectors = {
    topic: /\/topics\//,
    video: /\/videos\//,
    liveSession: /\/virtual-training\//,
    selfGuided: /\/self-guided-learning\//
  };
  const ret = { ...result };
  ret.type = 'unknown';
  ret.icon = searchImages.defaultImage;

  Object.entries(detectors).forEach((matcher) => {
    const [type, regex] = matcher;
    if (ret.uri.match(regex)) {
      ret.type = type;
      ret.icon = searchImages[`${type}Image`];
    }
  });

  return ret;
};

const runQuery = async (text, filters, resultsPerPage, setNumResults, currentPage) => {
  const rawResponse = await coveoSearch(text, filters, resultsPerPage, currentPage * resultsPerPage);
  const response = await rawResponse.json();

  const count = response.totalCount;
  setNumResults(() => count);
  return response.results;
};

function PageSearch({ searchImages }) {
  const router = useRouter();
  const { t, lang } = useTranslation();

  const [results, setResults] = useState(null);
  const [resultsPerPage, setResultsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [numResults, setNumResults] = useState(0);
  const [filters, setFilters] = useState(getInitialFilters(t, lang));
  const resultsRef = useRef(null);

  const query = router.query.q;
  const [search, setSearch] = useState('');
  const [currentSearch, setCurrentSearch] = useState('');

  const numPages = Math.ceil(numResults / resultsPerPage);

  const queryFilters = translateFiltersToQuery(filters);

  const handleSearch = (text) => runQuery(text, queryFilters, resultsPerPage, setNumResults, currentPage)
    .then((res) => res.map((result) => applyResultType(result, searchImages)))
    .then(setResults)
    .then((result) => {
      setCurrentSearch(() => text);
      return result;
    })
    .catch(() => {
      setCurrentSearch(() => text);
      setResults([]);
  });

  // next.js initially gives you a router with a blank query,
  // and then updates it to have the actual query on later renders.
  useEffect(() => {
    if (query && query.length) {
      handleSearch(query);
      setSearch(() => query);
    }
  }, [query]);

  // Redo the search if the user changes the filter
  useEffect(() => {
    handleSearch(search);
  }, [queryFilters]);

  // Redo the search and scroll back up if results per page or current page change,
  // but we only want to do this post-first render.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (!isFirstRender.current) {
      handleSearch(search).then(() => {
        if (resultsRef.current) {
          resultsRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      });
    }

    if (isFirstRender.current) {
      isFirstRender.current = false;
    }
  }, [resultsPerPage, currentPage]);

  const firstResult = currentPage * resultsPerPage + 1;
  const lastResult = Math.min(currentPage * resultsPerPage + resultsPerPage, numResults);

  return (
    <div data-testid="PageSearch">
      <section className={styles.searchHero}>
        <div className={styles.pageContent}>
          <h2 className={styles.pageHeader} ref={resultsRef}>
            {t('common:searchHeader')}
          </h2>
          <SearchInputUI size="lg" value={search} setValue={setSearch} onSearch={handleSearch} />
        </div>
      </section>

      <div className={styles.breadcrumbContainer}>
        <Breadcrumb breadcrumbs={[{ text: t('common:searchBreadcrumb'), href: '/search' }]} />
      </div>

      {results !== null && (
        <section>
          <div className={styles.pageContent}>
            <div className={styles.resultContent}>
              <section className={styles.filters}>
                {Object.entries(filters).map(([filterName, filterConfig]) => {
                  return (
                    <FilterCard
                      key={filterName}
                      filterName={filterName}
                      filterConfig={filterConfig}
                      onFilterSelected={(filter, value, selected) => {
                        setFilters((prev) => {
                          return reduceFilters(prev, filter, value, selected);
                        });
                      }}
                    />
                  );
                })}
              </section>
              <section className={styles.results}>
                {!results.length && (
                  <div className={styles.searchResultStatus}>
                    {t('common:searchNoResults', { search: currentSearch })}
                  </div>
                )}
                {!!results.length && (
                  <>
                    <div className={styles.searchResultStatus}>
                      {t('common:searchResultStatus', { firstResult, lastResult, numResults, search: currentSearch })}
                    </div>

                    <SearchResults data-testid="SearchResults" results={results} />
                    <SearchControls
                      data-testid="SearchControls"
                      onPageSizeChange={setResultsPerPage}
                      currentPageSize={resultsPerPage}
                      onPageChange={setCurrentPage}
                      currentPage={currentPage}
                      numPages={numPages}
                    />
                  </>
                )}
              </section>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

PageSearch.propTypes = PageSearchPropTypes;

export default PageSearch;
